import React, {useEffect, useState} from 'react';
import {useIntl} from 'react-intl';
import PropTypes from 'prop-types';
import {graphql} from 'react-relay';
import styled from 'styled-components';
import moment from 'moment';
import {BasicNumberInput, CurrencyInput, InvoiceRowHeader, PercentageInput} from '@/web-components';
import InvoiceRow from '../../../../components/invoice-row/Invoice_row';
import CustomScrollDiv from '../../../../forecast-app/shared/components/scroll-bars/custom_scroll_div';
import {
	AddLineButton,
	DetailLabel,
	DetailLabelColumn,
	DetailValue,
	DetailValueColumn,
	ErrorMessage,
	InputWithErrorWrapper,
	InvoiceDetails,
	InvoiceLinesDetailsSection,
	InvoiceLinesTableContainer,
	LargeTextArea,
	ProjectDropdownCell,
	TextArea,
} from '../../../../styles/sections/insights/invoicing/invoicing_page_styled';
import AddButton from '../../../../forecast-app/shared/components/add_button';
import Dropdown from '../../../../forecast-app/shared/components/dropdowns/dropdown';
import Util from '../../../../forecast-app/shared/util/util';
import TooltipContainer from '../../../../forecast-app/shared/components/tooltips/tooltip_container';
import {WarningIcon} from '../../../../images/WarningIcon';
import {QUANTITY_LOCKED_REASON} from '../../../../constants';
import ProjectDropdownRelay from '../../../../forecast-app/shared/components/dropdowns/project-dropdown-b/Project_dropdown';
import {generateLines} from '../../../../components/insights/invoicing/line-generation/invoicing_generate_lines';
import {convertPriceFromInvoiceCurrency} from '../../../../components/insights/invoicing/line-generation/invoicing_generate_lines_utils';

const MAX_DESCRIPTION_LENGTH = 1000;

/**
 * Applies a unit-price edit to a line, keeping a Fixed Price Deliverable line's ledger amount in
 * step with it.
 *
 * The typed value is stored as typed, even when it exceeds what the deliverable has left to bill —
 * an amount on an invoice is never silently rewritten. An over-budget line is reported by
 * hasDeliverableAmountError and blocks the invoice until the user changes it themselves.
 *
 * A value equal to the cap sends the deliverable's remaining amount verbatim instead of converting
 * the displayed figure back, so leaving the price alone on a cross-currency invoice cannot leave a
 * rounding remainder that could never be billed.
 *
 * Returns a new line and does not mutate the one passed in. A line with no deliverable amounts just
 * takes the new unit price.
 */
export const applyDeliverableAmountEdit = (line, unitPrice, projectCurrency, company, invoice) => {
	if (!line.deliverableAmounts || line.deliverableAmounts.length === 0) {
		return {...line, unitPrice};
	}

	const converted =
		unitPrice === line.maxUnitPrice
			? line.maxAmount
			: convertPriceFromInvoiceCurrency(unitPrice, projectCurrency, company, invoice);

	// A price within its own cap is capped in the ledger too. maxUnitPrice is the remaining amount
	// converted and rounded, so on a cross-currency invoice it can round up, and converting a price
	// just under it back can land a hair above what is actually left — refused by the server for an
	// input the field accepted. A price *over* the cap is left exactly as converted: the line is
	// already blocked, and nothing it holds is ever submitted.
	const isWithinCap = line.maxUnitPrice == null || unitPrice <= line.maxUnitPrice;
	const amount = isWithinCap && line.maxAmount != null ? Math.min(converted, line.maxAmount) : converted;

	return {
		...line,
		unitPrice,
		deliverableAmounts: line.deliverableAmounts.map(deliverableAmount => ({...deliverableAmount, amount})),
	};
};

const MAX_AMOUNT_DECIMALS = 2;

const decimalPlaces = value => {
	const text = String(value);
	const separator = text.indexOf('.');
	return separator === -1 ? 0 : text.length - separator - 1;
};

/**
 * Why a Fixed Price Deliverable line cannot be billed as it stands, or null when it can.
 *
 * One of: 'notPositive' (nothing to bill), 'tooManyDecimals' (finer than the ledger records), or
 * 'exceedsRemaining' (more than the deliverable has left). Each would be refused by the backend, so
 * the invoice is blocked here with an explanation against the field instead of being submitted and
 * bounced with a correlation id.
 *
 * Always null for a line that bills no deliverable; every other kind of line is unconstrained.
 */
export const deliverableAmountError = entry => {
	if (!entry.deliverableAmounts || entry.deliverableAmounts.length === 0) {
		return null;
	}
	if (!(entry.unitPrice > 0)) {
		return 'notPositive';
	}
	if (decimalPlaces(entry.unitPrice) > MAX_AMOUNT_DECIMALS) {
		return 'tooManyDecimals';
	}
	if (entry.maxUnitPrice != null && entry.unitPrice > entry.maxUnitPrice) {
		return 'exceedsRemaining';
	}
	return null;
};

export const hasDeliverableAmountError = entry => deliverableAmountError(entry) !== null;

/**
 * The copy produced by duplicating an invoice line: a new, unsaved entry that bills nothing already
 * billed elsewhere.
 *
 * A copy of a Fixed Price Deliverable line becomes a plain, freely editable line, because carrying
 * the link over would put two claims on the same deliverable in one invoice — which the backend
 * refuses, failing the whole invoice creation. Only that lock is dropped: a whole-project Fixed Price
 * or Retainer-period line is legitimately locked and its copy stays locked.
 */
export const duplicatedLine = line => {
	const copy = {...line, id: null, timeRegIds: []};

	if (copy.quantityLockedReason === QUANTITY_LOCKED_REASON.FIXED_PRICE_DELIVERABLE) {
		copy.deliverableIds = undefined;
		copy.deliverableAmounts = undefined;
		copy.maxUnitPrice = undefined;
		copy.maxAmount = undefined;
		copy.quantityLockedReason = null;
	}

	return copy;
};

// NB: Lines is the same as entries, please do not mind the mix in the naming, we kind of decided midway
import {WorkDetailPanel} from './work_detail_panel';
import {WorkDetailOverlapNotice} from './work_detail_overlap_notice';
import {
	buildWorkDetailRows,
	preserveWorkDetailRemovals,
} from '../../../../components/insights/invoicing/line-generation/invoicing_work_detail_rows';
import {workDetailPriceConverter} from '../work_detail_currency';
import {canSnapshotWorkDetailRates} from '../work_detail_rates';
import {hasFeatureFlag} from '../../../../forecast-app/shared/util/FeatureUtil';
import {tokensForThemeProvider} from '@forecast-it/design-system';

// Separated from the priced line items above by a rule and spacing: the work detail states what was
// done and must not read as another set of charges.
const WorkDetailSection = styled.div`
	margin-top: 24px;
	padding-top: 16px;
	border-top: 1px solid ${tokensForThemeProvider.sky.sys.color.border.decorative};
`;

const InvoiceEntriesList = ({
	canEditInvoice,
	invoiceExported,
	invoiceExporting,
	lines,
	setInvoiceLines,
	projects,
	clientProjects,
	currency,
	tax,
	discount,
	integrationData,
	setHasError,
	viewer,
	config,
	invoice,
	company,
	isProjectInvoicing,
	project,
	isCreatingBlankInvoice,
	// Off everywhere but invoice creation. Edits to a saved invoice go through
	// UpdateInvoiceEntryMutation, which does not maintain the deliverable's recorded amount, so
	// unlocking the price there would let the ledger drift from what the client was billed.
	allowEditingDeliverableAmount = false,
	workDetailRows = [],
	setWorkDetailRows,
	workDetailOverlaps = [],
}) => {
	const intl = useIntl();
	const {formatMessage} = intl;

	const columns = {
		project: true,
		description: true,
		xeroAccount: integrationData?.xero?.enabled,
		quickbooksItem: integrationData?.quickbooks?.enabled,
		economicProduct: integrationData?.economic?.enabled,
		sageIntacctLocation: integrationData?.sageIntacct?.enabled,
		sageIntacctAccount: integrationData?.sageIntacct?.enabled,
		quantity: true,
		unitPrice: true,
		tax: true,
		discount: true,
		totalPrice: true,
		hasContextMenu: true,
	};
	let entryScrollDiv = null;

	const [descriptionFocused, setDescriptionFocused] = useState(null);

	const isInvoiceLocked = !canEditInvoice || invoiceExported || invoiceExporting ? true : null;
	const invoiceEntryLineCustomHeight = isInvoiceLocked ? 50 : null;
	const invoiceEntryLineCustomWidth = isInvoiceLocked ? (invoiceExporting ? 290 : 400) : null;

	// Whether the invoice may be saved is a property of every line, not of the last one edited, so it
	// is derived from the whole list. Setting it per-edit meant a second row's edit could clear a
	// first row's error and let the invoice through.
	//
	// setHasError is optional: the export modals mount this grid read-only and have nothing to gate,
	// so they never pass it. Calling it unconditionally from an effect crashes them on open, which the
	// old per-edit call never did because their inputs are disabled and so unreachable.
	useEffect(() => {
		const hasAnyError = lines.some(
			line =>
				line.description?.length > MAX_DESCRIPTION_LENGTH ||
				(allowEditingDeliverableAmount && hasDeliverableAmountError(line))
		);
		setHasError?.(hasAnyError);
	}, [lines, allowEditingDeliverableAmount, setHasError]);

	useEffect(() => {
		if (viewer) {
			let fetchedUnInvoicedTimeRegistrations = [];
			const projectPersonsMap = viewer.company.allProjects.edges.reduce(function (map, obj) {
				map[obj.node.id] = obj.node.projectPersons;
				return map;
			}, {});
			viewer.company.allProjects.edges.forEach(
				project =>
					(fetchedUnInvoicedTimeRegistrations = fetchedUnInvoicedTimeRegistrations.concat(
						project.node.unInvoicedTimeRegistrations
					))
			);
			setInvoiceLines(prevLines => {
				const deliverableLines = prevLines.filter(line => line.deliverableIds && line.deliverableIds.length > 0);
				const generatedLines = isCreatingBlankInvoice
					? []
					: generateLines(
							config,
							intl,
							invoice,
							fetchedUnInvoicedTimeRegistrations,
							projects,
							projectPersonsMap,
							company
					  );
				return [...generatedLines, ...deliverableLines];
			});
		}
	}, [viewer]);

	const workDetailEnabled = hasFeatureFlag('work_detail_on_fixed_price_invoices');

	const workDetailWindow = {
		startDate: config && config.workDetailStartDate ? moment(config.workDetailStartDate).format('YYYY-MM-DD') : undefined,
		endDate: config && config.workDetailEndDate ? moment(config.workDetailEndDate).format('YYYY-MM-DD') : undefined,
	};

	// Project.timeRegistrations returns an EMPTY connection for a project the viewer lacks full access
	// to, so an empty snapshot there is explained by access rather than by nobody having logged time.
	// The panel needs to know which, or its empty state states a fact it cannot see.
	const hasRestrictedProject = (projects || [])
		.filter(p => !config || !config.selectedProjectsIds?.length || config.selectedProjectsIds.includes(p.id))
		.some(p => p.fullAccessToProject === false);

	// Re-materialises the work-detail snapshot whenever the grouping or the window changes, and on
	// mount — stepping back through the flow unmounts this panel, so the way forward again is the only
	// place a changed grouping can take effect.
	//
	// Removals are carried across the rebuild by key rather than reset, because that rebuild is not
	// something the operator asked for. A key encodes the work behind the row, so removals still
	// vanish when the grouping changes and there is nothing left for them to refer to.
	useEffect(() => {
		if (!viewer || !workDetailEnabled || !setWorkDetailRows) return;
		if (!config || !config.includeWorkDetail) {
			setWorkDetailRows([]);
			return;
		}
		const selectedIds = config.selectedProjectsIds || [];
		const timeRegs = viewer.company.allProjects.edges
			.filter(edge => selectedIds.length === 0 || selectedIds.includes(edge.node.id))
			.flatMap(edge => (edge.node.timeRegistrations ? edge.node.timeRegistrations.edges.map(e => e.node) : []));

		const rows = buildWorkDetailRows(timeRegs, {
			grouping: config.timeRegGrouping,
			showRates: canSnapshotWorkDetailRates(company),
			quantityDecimals: (company && company.invoiceQuantityDecimals) || 2,
			startDate: workDetailWindow.startDate,
			endDate: workDetailWindow.endDate,
			priceToInvoiceCurrency: workDetailPriceConverter(company, invoice),
		});
		setWorkDetailRows(previousRows => preserveWorkDetailRemovals(rows, previousRows));
	}, [
		viewer,
		workDetailEnabled,
		config && config.includeWorkDetail,
		config && config.timeRegGrouping,
		workDetailWindow.startDate,
		workDetailWindow.endDate,
		// Re-prices when the operator changes the invoice's currency: rate and value are stated in it,
		// so the same work converts to different amounts.
		invoice && invoice.currency,
	]);

	const handleDescriptionBlur = e => {
		setDescriptionFocused(null);
		e.target.scrollTop = 0;
	};

	const updateEntry = (entryIndex, field, value) => {
		let updatedEntry = {...lines[entryIndex]};
		switch (field) {
			case 'project':
				updatedEntry.projectId = value;
				break;
			case 'description':
				updatedEntry.description = value;
				break;
			case 'xeroAccount':
				updatedEntry.xeroAccount = value.value;
				updatedEntry.tax = value.taxRate;
				updatedEntry.accountCode = value.code;
				break;
			case 'quickbooksItem':
				updatedEntry.quickbooksItem = value.value;
				updatedEntry.quickbooksDescription = value.description;
				updatedEntry.quickbooksTaxable = value.taxable;
				break;
			case 'economicProduct':
				updatedEntry.economicProduct = value.value;
				break;
			case 'sageIntacctLocation':
				const timeReg = integrationData?.sageIntacct?.mappedTimeRegistrations.find(
					timeReg => timeReg.invoiceEntryId === Util.getIdFromBase64String(updatedEntry.id)
				);
				let locationIdPresent = false;
				if (timeReg !== undefined) {
					if (timeReg.person.sageIntacctLocationId !== null) {
						locationIdPresent = true;
					}
				}
				if (locationIdPresent) {
					updatedEntry.sageIntacctLocation = integrationData?.sageIntacct?.locations.find(
						location => location.value === timeReg.person.sageIntacctLocationId
					).value;
				} else {
					updatedEntry.sageIntacctLocation = value.value;
				}
				break;
			case 'sageIntacctAccount':
				updatedEntry.sageIntacctAccount = value.value;
				break;
			case 'quantity':
				updatedEntry.quantity = value;
				break;
			case 'price': {
				const entryProject = projects.find(candidate => candidate.id === updatedEntry.projectId);
				// company is optional: the export modals mount this grid without it. They also disable
				// every input, so this branch is unreachable there — but it must not be the thing that
				// would crash if that ever changed.
				const projectCurrency = entryProject?.rateCard ? entryProject.rateCard.currency : company?.currency;
				updatedEntry = applyDeliverableAmountEdit(updatedEntry, +value, projectCurrency, company, invoice);
				break;
			}
			case 'tax':
				updatedEntry.tax = +value * 100;
				break;
			case 'discount':
				updatedEntry.discount = +value * 100;
				break;
			default:
				break;
		}

		const updatedLines = [...lines];
		updatedLines.splice(entryIndex, 1, updatedEntry);

		setInvoiceLines(updatedLines);
	};

	const duplicateEntry = index => {
		const newEntry = duplicatedLine(lines[index]);

		const newLines = [...lines];
		newLines.splice(index, 0, newEntry);

		setInvoiceLines(newLines);
	};

	const deleteEntry = index => {
		const newLines = [...lines];
		newLines.splice(index, 1);

		setInvoiceLines(newLines);
	};

	const getWarningTooltipText = (canEditInvoice, invoiceExported, invoiceExporting, warning) => {
		if (canEditInvoice === false)
			return [
				{
					title: 'invoicing.cannot_edit_warning_title',
					description: formatMessage({id: 'invoicing.cannot_edit_warning'}, {invoiceStatus: invoice?.status}),
				},
			];
		if (invoiceExported)
			return [
				{
					title: 'invoicing.cannot_edit_warning_title_exported',
					description: formatMessage({id: 'invoicing.cannot_edit_warning_exported'}),
				},
			];
		if (invoiceExporting)
			return [
				{
					title: 'invoicing.cannot_edit_warning_title_exporting',
					description: formatMessage({id: 'invoicing.cannot_edit_warning_exporting'}),
				},
			];
		if (warning) return [warning];
		return null;
	};

	const getProjectDropdown = (index, showWarning) => {
		const noClientProjects = clientProjects.length === 0;
		return (
			<ProjectDropdownCell maxWidth={invoiceExporting ? 152 : null} customHeight={invoiceEntryLineCustomHeight}>
				{showWarning && (
					<TooltipContainer
						infoText={formatMessage({
							id: noClientProjects ? 'invoicing.no_client_projects' : 'invoicing.no_project_entry_warning',
						})}
					>
						<WarningIcon />
					</TooltipContainer>
				)}
				<ProjectDropdownRelay
					hideEmptyGroups
					projects={clientProjects.map(project => {
						return {node: project};
					})}
					selectedItems={lines[index].projectId ? [lines[index].projectId] : []}
					isMultiSelect={false}
					onSelect={option => updateEntry(index, 'project', option[0])}
					maxHeight={200}
					disabled={noClientProjects}
					isClearable={false}
					usePortal
					dropdownAlignment={'left'}
				/>
			</ProjectDropdownCell>
		);
	};

	const getDescriptionField = (entry, index, hasDescriptionError) => {
		return isInvoiceLocked ? (
			<TooltipContainer infoText={entry.description} triangleLocation={'right'}>
				<LargeTextArea
					disabled={isInvoiceLocked}
					value={entry.description || ''}
					onBlur={handleDescriptionBlur}
					onFocus={() => {
						setDescriptionFocused(index);
					}}
					rows={descriptionFocused === index ? 8 : 1}
					onChange={e => updateEntry(index, 'description', e.target.value)}
					error={hasDescriptionError}
					customWidth={invoiceEntryLineCustomWidth}
					data-cy={'invoice-entry-description'}
				/>
				{hasDescriptionError ? (
					<ErrorMessage>{intl.formatMessage({id: 'invoicing.description_length_error'})}</ErrorMessage>
				) : null}
			</TooltipContainer>
		) : (
			<TooltipContainer infoText={getWarningTooltipText(canEditInvoice, invoiceExported, invoiceExporting, null)}>
				<TextArea
					disabled={isInvoiceLocked}
					value={entry.description || ''}
					onBlur={handleDescriptionBlur}
					onFocus={() => {
						setDescriptionFocused(index);
					}}
					rows={descriptionFocused === index ? 8 : 1}
					onChange={e => updateEntry(index, 'description', e.target.value)}
					error={hasDescriptionError}
					data-cy={'invoice-entry-description'}
				/>
				{hasDescriptionError ? (
					<ErrorMessage>{intl.formatMessage({id: 'invoicing.description_length_error'})}</ErrorMessage>
				) : null}
			</TooltipContainer>
		);
	};

	const getQuantityField = (entry, index) => {
		let warning = null;
		switch (entry.quantityLockedReason) {
			case QUANTITY_LOCKED_REASON.FIXED_PRICE:
				warning = {title: 'common.fixed_price', description: intl.formatMessage({id: 'invoicing.fixed_price_warning'})};
				break;
			case QUANTITY_LOCKED_REASON.FIXED_PRICE_PERIOD:
				warning = {
					title: 'common.fixed_price_period',
					description: intl.formatMessage({id: 'invoicing.fixed_price_period_warning'}),
				};
				break;
			case QUANTITY_LOCKED_REASON.FIXED_PRICE_DELIVERABLE:
				warning = {
					title: 'common.fixed_price_deliverable',
					description: intl.formatMessage({id: 'invoicing.fixed_price_deliverable_warning'}),
				};
				break;
			default:
				break;
		}
		return (
			<InputWithErrorWrapper>
				<TooltipContainer
					infoText={
						!isInvoiceLocked
							? getWarningTooltipText(canEditInvoice, invoiceExported, invoiceExporting, warning)
							: null
					}
				>
					<BasicNumberInput
						disabled={warning || isInvoiceLocked}
						shortInput
						hideLabel
						width={'42px'}
						id={`quantity-${entry.id}`}
						type="number"
						value={entry.quantity || ''}
						callback={quantity => updateEntry(index, 'quantity', quantity)}
						alignTextRight={true}
						maxLength={8}
						noBorder={isInvoiceLocked}
						noLocked={isInvoiceLocked}
						preventNumberConversion
						cy={'invoice-entry-quantity'}
					/>
				</TooltipContainer>
			</InputWithErrorWrapper>
		);
	};

	const getUnitPriceField = (entry, index) => {
		let warning = null;
		const isFixedPriceDeliverable = entry.quantityLockedReason === QUANTITY_LOCKED_REASON.FIXED_PRICE_DELIVERABLE;
		const isDeliverableAmountEditable = isFixedPriceDeliverable && allowEditingDeliverableAmount;
		if (isFixedPriceDeliverable) {
			warning = {
				title: 'common.fixed_price_deliverable',
				description: intl.formatMessage({
					id: isDeliverableAmountEditable
						? 'invoicing.fixed_price_deliverable_amount_capped'
						: 'invoicing.fixed_price_deliverable_warning',
				}),
			};
		} else if (entry.unitPrice === 0.0) {
			const project = projects.find(project => project.id === entry.projectId);
			const isFromExpense = !!entry.expenseItemIds;
			if (project && !project.rateCard && !isFromExpense) {
				warning = {
					title: 'invoicing.no_rate_card_warning_title',
					description: intl.formatMessage({id: 'invoicing.no_rate_card_warning_description'}),
				};
			} else {
				warning = {
					title: 'invoicing.no_unit_price_warning_title',
					description: intl.formatMessage({id: 'invoicing.no_unit_price_warning_description'}),
				};
			}
		}
		const noEditInfoText = getWarningTooltipText(canEditInvoice, invoiceExported, invoiceExporting, null);
		const isDisabled = (isFixedPriceDeliverable && !allowEditingDeliverableAmount) || isInvoiceLocked;
		const amountError = isDeliverableAmountEditable ? deliverableAmountError(entry) : null;
		const hasAmountError = amountError !== null;
		// This column is only 82px wide and InputWithErrorWrapper lays its children out in a row, so a
		// text message beside the field is clipped by the column and never seen. The reason travels on
		// the icon's own tooltip instead, and the disabled Save button repeats it where a blocked user
		// will look for it.
		const amountErrorText =
			amountError === 'exceedsRemaining'
				? intl.formatMessage(
						{id: 'invoicing.fixed_price_deliverable_amount_exceeds_remaining'},
						{remaining: Util.GetFormattedCurrencySymbol(currency, entry.maxUnitPrice)}
				  )
				: amountError === 'tooManyDecimals'
				? intl.formatMessage({id: 'invoicing.fixed_price_deliverable_amount_too_many_decimals'})
				: amountError === 'notPositive'
				? intl.formatMessage({id: 'invoicing.fixed_price_deliverable_amount_must_be_positive'})
				: null;
		// An editable cell's cap explanation is guidance, not a fault, so it rides on the input's own
		// tooltip rather than a danger icon — and it must reach that tooltip, which is what
		// noEditInfoText is null for in the create modal. Suppressed while the field is in error, since
		// the error is the more specific thing to say.
		const showWarningIcon = warning && !noEditInfoText && !isDeliverableAmountEditable;
		const inputInfoText = !isInvoiceLocked
			? noEditInfoText || (isDeliverableAmountEditable && !hasAmountError && warning ? [warning] : null)
			: null;
		return (
			<InputWithErrorWrapper>
				{hasAmountError && (
					<TooltipContainer infoText={[{title: 'common.fixed_price_deliverable', description: amountErrorText}]}>
						<WarningIcon danger title={amountErrorText} cy={'invoice-entry-deliverable-amount-error'} />
					</TooltipContainer>
				)}
				{showWarningIcon && (
					<TooltipContainer infoText={[warning]}>
						<WarningIcon danger />
					</TooltipContainer>
				)}
				<TooltipContainer infoText={inputInfoText}>
					<CurrencyInput
						disabled={isDisabled}
						hasError={hasAmountError}
						shortInput
						hideLabel
						width={showWarningIcon || hasAmountError ? '59px' : '82px'}
						id={`unitPrice-${entry.id}`}
						type="number"
						suffix={Util.GetCurrencySymbol(currency)}
						value={entry.unitPrice || ''}
						callback={value => updateEntry(index, 'price', value)}
						currencySymbole={Util.GetCurrencySymbol(currency)}
						symbolInFront={Util.CurrencyIsPrefixed(Util.GetCurrencySymbol(currency), currency)}
						intl={intl}
						maxLength={8}
						noBorder={isDisabled}
						noLocked={isDisabled}
						cy={'invoice-entry-unit-price'}
					/>
				</TooltipContainer>
			</InputWithErrorWrapper>
		);
	};

	const getTaxField = (entry, index) => {
		return (
			<TooltipContainer
				infoText={
					!isInvoiceLocked ? getWarningTooltipText(canEditInvoice, invoiceExported, invoiceExporting, null) : null
				}
			>
				<PercentageInput
					disabled={isInvoiceLocked}
					shortInput
					hideLabel
					width={'47px'}
					id={`tax-${entry.id}`}
					type="number"
					value={entry.tax || 0}
					callback={value => updateEntry(index, 'tax', value)}
					alignTextRight
					showDecimals={true}
					maxLength={3}
					noBorder={isInvoiceLocked}
					noLocked={isInvoiceLocked}
					cy={'invoice-entry-tax'}
				/>
			</TooltipContainer>
		);
	};

	const getDiscountField = (entry, index) => {
		return (
			<TooltipContainer
				infoText={
					!isInvoiceLocked ? getWarningTooltipText(canEditInvoice, invoiceExported, invoiceExporting, null) : null
				}
			>
				<PercentageInput
					disabled={isInvoiceLocked}
					shortInput
					hideLabel
					width={'47px'}
					id={`discount-${entry.id}`}
					type="number"
					value={entry.discount || 0}
					callback={value => updateEntry(index, 'discount', value)}
					alignTextRight
					showDecimals={true}
					maxLength={3}
					noBorder={isInvoiceLocked}
					noLocked={isInvoiceLocked}
					cy={'invoice-entry-discount'}
				/>
			</TooltipContainer>
		);
	};

	const getMenuActions = index => {
		return [
			{
				text: formatMessage({id: 'common.duplicate'}),
				onClick: () => duplicateEntry(index),
				disabled: isInvoiceLocked,
			},
			{
				text: formatMessage({id: 'common.delete'}),
				onClick: () => deleteEntry(index),
				disabled: isInvoiceLocked,
			},
		];
	};

	const scrollToBottom = () => {
		if (entryScrollDiv) {
			entryScrollDiv.animateScrollTop(entryScrollDiv.scrollbars.getScrollHeight());
		}
	};

	const addLine = () => {
		const newLineList = [
			...lines,
			{
				projectId: isProjectInvoicing ? project.id : null,
				description: '',
				discount: discount || 0,
				name: '',
				quantity: (1).toFixed(company?.invoiceQuantityDecimals || 2),
				tax: tax || 0,
				unitPrice: 0,
				isBeingAdded: true,
			},
		];

		setInvoiceLines(newLineList);
		scrollToBottom();
	};

	const invoiceAmounts = Util.getInvoiceTotals(lines);

	const getXeroAccountDropdown = (entry, index) => {
		const xeroAccounts = integrationData?.xero?.accounts || [];
		return (
			<Dropdown
				customHeight={28}
				customWidth={100}
				hideLabel
				disabled={xeroAccounts.length === 0}
				value={entry.xeroAccount}
				options={xeroAccounts}
				onChange={v => updateEntry(index, 'xeroAccount', v)}
			/>
		);
	};

	const getQuickbooksItemDropdown = (entry, index) => {
		const quickbooksItems = integrationData?.quickbooks?.itemsAndServices || [];
		return (
			<Dropdown
				customHeight={28}
				customWidth={100}
				required={true}
				hideLabel
				value={entry.quickbooksItem}
				options={quickbooksItems}
				onChange={v => updateEntry(index, 'quickbooksItem', v)}
				placeholder={formatMessage({id: 'common.required_field'})}
			/>
		);
	};

	const getEconomicProductDropdown = (entry, index) => {
		const economicProducts = integrationData?.economic?.products || [];
		return (
			<Dropdown
				customHeight={28}
				customWidth={130}
				hideLabel
				value={entry.economicProduct}
				options={economicProducts}
				onChange={v => updateEntry(index, 'economicProduct', v)}
			/>
		);
	};

	const getSageIntacctLocationDropdown = (entry, index) => {
		const sageIntacctLocations = integrationData?.sageIntacct?.locations || [];
		const timeReg = integrationData?.sageIntacct?.mappedTimeRegistrations.find(
			timeReg => timeReg.invoiceEntryId === Util.getIdFromBase64String(entry.id)
		);
		let locationIdPresent = false;

		if (timeReg !== undefined) {
			if (timeReg.person.sageIntacctLocationId !== null) {
				locationIdPresent = true;
			}
		}

		if (sageIntacctLocations.length > 1 && locationIdPresent) {
			const value = sageIntacctLocations.find(location => location.value === timeReg.person.sageIntacctLocationId).value;
			if (value !== entry.sageIntacctLocation) {
				updateEntry(index, 'sageIntacctLocation', value);
			}
		}

		return (
			<Dropdown
				customHeight={28}
				customWidth={130}
				hideLabel
				value={entry.sageIntacctLocation}
				options={sageIntacctLocations}
				disabled={locationIdPresent}
				onChange={v => updateEntry(index, 'sageIntacctLocation', v)}
				placeholder={
					sageIntacctLocations.length > 1 && locationIdPresent
						? sageIntacctLocations.find(location => location.value === timeReg.person.sageIntacctLocationId).label
						: ''
				}
			/>
		);
	};

	const getSageIntacctAccountDropdown = (entry, index) => {
		const sageIntacctAccounts = integrationData?.sageIntacct?.accounts || [];
		return (
			<Dropdown
				customHeight={28}
				customWidth={130}
				hideLabel
				value={entry.sageIntacctAccount}
				options={sageIntacctAccounts}
				onChange={v => updateEntry(index, 'sageIntacctAccount', v)}
			/>
		);
	};

	return (
		<>
			<InvoiceLinesTableContainer data-userpilot={'invoice-entries-list'}>
				<InvoiceRowHeader customWidth={invoiceEntryLineCustomWidth} intl={intl} columns={columns} isEntryRow={true} />
				<CustomScrollDiv
					autoHeightMin={100}
					autoHeightMax={400}
					ref={CustomScrollDiv => (entryScrollDiv = CustomScrollDiv)}
				>
					{lines.map((entry, index) => {
						const hasDescriptionError = entry.description?.length > MAX_DESCRIPTION_LENGTH;
						return (
							<InvoiceRow
								key={index}
								columns={columns}
								project={projects.find(project => project.id === entry.projectId) || project}
								noProjectName={true}
								description={getDescriptionField(entry, index, hasDescriptionError)}
								hasDescriptionError={hasDescriptionError}
								quantity={getQuantityField(entry, index)}
								unitPrice={getUnitPriceField(entry, index)}
								tax={getTaxField(entry, index)}
								discount={getDiscountField(entry, index)}
								totalPrice={Util.getFormattedNumberWithCurrency(
									Util.GetCurrencySymbol(currency),
									Util.getInvoiceEntryAmount(entry.unitPrice, entry.quantity, entry.discount, entry.tax),
									intl
								)}
								contextMenuOptions={getMenuActions(index)}
								customProjectField={
									(entry.isBeingAdded || !entry.projectId) && !isProjectInvoicing
										? getProjectDropdown(index, !entry.projectId)
										: null
								}
								isFirstRow={index === 0}
								isEntryRow={true}
								isInModal={true}
								xeroAccount={getXeroAccountDropdown(entry, index)}
								quickbooksItem={getQuickbooksItemDropdown(entry, index)}
								economicProduct={getEconomicProductDropdown(entry, index)}
								sageIntacctLocation={getSageIntacctLocationDropdown(entry, index)}
								sageIntacctAccount={getSageIntacctAccountDropdown(entry, index)}
								customHeight={invoiceEntryLineCustomHeight}
								customWidth={invoiceEntryLineCustomWidth}
								showMeatballMenu={isInvoiceLocked}
								customHoverBackground={isInvoiceLocked}
								projectNameTooltip={isInvoiceLocked}
								cy={'invoice-line-' + index}
							/>
						);
					})}
				</CustomScrollDiv>
			</InvoiceLinesTableContainer>
			<InvoiceLinesDetailsSection>
				{canEditInvoice && !invoiceExported && !invoiceExporting && (
					<AddLineButton data-userpilot={'add-entry-button'}>
						<AddButton onClick={addLine} cy={'add-line-button'} />
					</AddLineButton>
				)}
				<InvoiceDetails>
					<DetailLabelColumn>
						<DetailLabel>{formatMessage({id: 'common.subtotal'}) + ':'}</DetailLabel>
						<DetailLabel>{formatMessage({id: 'project_section.xero_modal_table_discount'}) + ':'}</DetailLabel>
						<DetailLabel>{formatMessage({id: 'common.tax'}) + ':'}</DetailLabel>
						<DetailLabel isTotal>{formatMessage({id: 'common.total_amount'}) + ':'}</DetailLabel>
					</DetailLabelColumn>
					<DetailValueColumn>
						<DetailValue>
							{Util.getFormattedNumberWithCurrency(
								Util.GetCurrencySymbol(currency),
								invoiceAmounts.subtotal,
								intl
							)}
						</DetailValue>
						<DetailValue>
							{Util.getFormattedNumberWithCurrency(
								Util.GetCurrencySymbol(currency),
								invoiceAmounts.discount,
								intl
							)}
						</DetailValue>
						<DetailValue>
							{Util.getFormattedNumberWithCurrency(Util.GetCurrencySymbol(currency), invoiceAmounts.tax, intl)}
						</DetailValue>
						<DetailValue isTotal data-cy={'invoice-entry-total'}>
							{Util.getFormattedNumberWithCurrency(Util.GetCurrencySymbol(currency), invoiceAmounts.total, intl)}
						</DetailValue>
					</DetailValueColumn>
				</InvoiceDetails>
			</InvoiceLinesDetailsSection>
			{workDetailEnabled && config && config.includeWorkDetail ? (
				<WorkDetailSection data-cy={'work-detail-section'}>
					{/* The same warning the period control on the previous step shows, repeated here as the
					    last look before saving. Nothing is blocked by it: re-issuing a corrected invoice
					    covers an already-reported period on purpose. */}
					<WorkDetailOverlapNotice overlaps={workDetailOverlaps} />
					<WorkDetailPanel
						rows={workDetailRows}
						showRates={canSnapshotWorkDetailRates(company)}
						quantityDecimals={(company && company.invoiceQuantityDecimals) || 2}
						onChange={setWorkDetailRows}
						grouping={config.timeRegGrouping}
						startDate={workDetailWindow.startDate}
						endDate={workDetailWindow.endDate}
						hasRestrictedProject={hasRestrictedProject}
					/>
				</WorkDetailSection>
			) : null}
		</>
	);
};

InvoiceEntriesList.propTypes = {
	workDetailRows: PropTypes.array,
	setWorkDetailRows: PropTypes.func,
	workDetailOverlaps: PropTypes.array,
	canEditInvoice: PropTypes.bool,
	invoiceExported: PropTypes.bool,
	invoiceExporting: PropTypes.bool,
	lines: PropTypes.array,
	setInvoiceLines: PropTypes.func,
	projects: PropTypes.array,
	clientProjects: PropTypes.array,
	currency: PropTypes.string,
	integrationData: PropTypes.shape({
		xero: PropTypes.shape({
			enabled: PropTypes.bool,
			accounts: PropTypes.array,
		}),
		quickbooks: PropTypes.shape({
			enabled: PropTypes.bool,
			itemsAndServices: PropTypes.array,
		}),
		economics: PropTypes.shape({
			enabled: PropTypes.bool,
			products: PropTypes.array,
		}),
		sageIntacct: PropTypes.shape({
			enabled: PropTypes.bool,
			locations: PropTypes.array,
			accounts: PropTypes.array,
		}),
	}),
	setHasError: PropTypes.func,
	config: PropTypes.object,
	invoice: PropTypes.object,
	company: PropTypes.object,
};

const invoiceEntriesListQuery = graphql`
	query invoiceEntriesList_Query(
		$searchQuery: TaskSearchQueryType
		$startYear: Int!
		$startMonth: Int!
		$startDay: Int!
		$endYear: Int!
		$endMonth: Int!
		$endDay: Int!
		$includeWorkDetail: Boolean!
	) {
		viewer {
			actualPersonId
			component(name: "invoice_entries_list")
			company {
				allProjects(first: 100000, searchQuery: $searchQuery) {
					edges {
						node {
							id
							projectPersons(first: 1000000) {
								edges {
									node {
										person {
											id
										}
										role {
											id
											name
										}
									}
								}
							}
							unInvoicedTimeRegistrations(
								startYear: $startYear
								startMonth: $startMonth
								startDay: $startDay
								endYear: $endYear
								endMonth: $endMonth
								endDay: $endDay
							) {
								id
								person {
									id
								}
								project {
									id
								}
								role {
									id
									name
								}
								task {
									id
									name
									project {
										id
									}
									phase {
										id
										name
									}
									sprint {
										id
										name
									}
									statusColumnV2 {
										category
									}
								}
								minutesRegistered
								billableMinutesRegistered
								year
								month
								day
								price
								priceBaseCurrency
								rate
								notes
							}
							# Work detail groups this, NOT unInvoicedTimeRegistrations above. That field is
							# whitelist-gated server-side to Time & Materials and Retainer projects and
							# returns nothing for a fixed-price one, which is the whole reason work
							# detail cannot reuse it. Also not invoice.timeRegistrations, which returns
							# only time that WAS marked invoiced and so is always empty here.
							#
							# Gated, because this is every registration of every selected project — by far
							# the heaviest thing this query asks for — and it is read only when the
							# operator has actually asked for work detail. Every other consumer of this
							# query passes false and pays nothing for it.
							#
							# Unpaginated for the same reason the PDF read path is: the snapshot built
							# from this IS the document, and its printed "Total hours" / "Total value"
							# are summed from whatever arrived. A page limit would not just drop rows,
							# it would silently understate the totals a client is handed.
							timeRegistrations @include(if: $includeWorkDetail) {
								edges {
									node {
										id
										year
										month
										day
										minutesRegistered
										# rate, not price: price is the billable portion only, and a
										# work-detail row states all the work that was done.
										rate
										notes
										person {
											id
											firstName
											lastName
										}
										role {
											id
											name
										}
										# The dimension PROJECT groups by, and the rate card that decides
										# which currency this registration's price is stated in. A single
										# invoice can span several projects, so neither can be resolved
										# from the invoice alone.
										#
										# Set only for time logged against the project directly. Time logged
										# against a task leaves this null and carries the project on the task
										# below, which is why both are selected — see workDetailProject.
										project {
											id
											name
											rateCard {
												id
												currency
											}
										}
										# statusColumnV2 keeps the done-tasks grouping to done tasks, the same
										# way the priced generator filters it.
										task {
											id
											name
											# Where task-logged time carries its project, so it groups and
											# converts like project-logged time instead of reading as having
											# no project at all. The uninvoiced selection above traverses the
											# same pair for the same reason.
											project {
												id
												name
												rateCard {
													id
													currency
												}
											}
											statusColumnV2 {
												category
											}
											phase {
												id
												name
											}
											sprint {
												id
												name
											}
										}
									}
								}
							}
							...DeprecatedProjectIndicatorJS_project
						}
					}
				}
			}
		}
	}
`;

export {invoiceEntriesListQuery};

/*
Not using a fragment container, because we use the data for bulkinvoicing without rendering this component.
If we use a fragment container, the data loaded in the fragment container won't be available for the bulk invoice entry generation
*/
export default InvoiceEntriesList;
